/**
 * contactStageOptions — which stages a CONTACT's record may show and offer.
 *
 * ── HOW A CONTACT'S PIPELINE IS DETERMINED (read this before changing anything)
 *
 * ⛔⛔ `crm_contacts` HAS NO `pipeline_id` COLUMN. It has one TEXT column,
 * `pipeline_stage` (database/schema.sql:479). A contact is therefore NOT a
 * member of a pipeline in the schema, and "the contact's pipeline" is a
 * DERIVED fact, not a stored one. The repo already recorded this once, at
 * routes/crm.js:6415 (2026-09-05): the pipeline card badges all rendered the
 * identical 6,295 because a per-pipeline contact count cannot be computed from
 * a table that does not carry the pipeline.
 *
 * `crm_opportunities` is the only table carrying `pipeline_id`
 * (database/migration_opportunities.sql:9) and it carries `contact_id` too
 * (:8). So the derivation is:
 *
 *     contact → its opportunities → the pipeline of the one that wins
 *
 * and the server (routes/crm.js, channel `crm:contacts:stage-options`) picks
 * the winner as: active, non-null pipeline_id, pipeline itself active,
 * preferring status='open', then most recently updated, then highest id. The
 * last key is not decoration — a LIMIT 1 without a TOTAL order returns a row
 * chosen by physical layout, which is the failure mapGhlStageToCanonical's
 * name tier was fixed for (database/ghlOpportunityUpsert.js:66).
 *
 * ⚠ A CONTACT WITH NO OPPORTUNITY HAS NO PIPELINE, and that is the common
 * case, not an edge: a lead that never became a deal never gets a row in
 * crm_opportunities. Those contacts fall back to the legacy short codes below.
 *
 * ── WHY THE REAL STAGES BELONG HERE AT ALL
 *
 * `crm_contacts.pipeline_stage` really does hold Carlos's canonical GHL stage
 * names, not only the short codes: routes/gohighlevel.js:1197 writes
 * `mappedStage` — the output of mapGhlStageToCanonical, i.e. the row's own
 * `crm_pipeline_stages.name` — straight into the column. A hard-coded label
 * map therefore cannot name what the column already contains, which is what
 * Maria saw.
 */

/**
 * The pre-taxonomy short codes, for contacts with no pipeline.
 *
 * ⚠ This is the CONTACT vocabulary (schema.sql:479's original CHECK), NOT the
 * seven opportunity slugs in src/lib/opportunityStages.js. They overlap and
 * they are not the same list; conflating them is how a contact filter comes to
 * offer 'negotiation', which no contact row has ever held.
 */
export const LEGACY_CONTACT_STAGES = Object.freeze([
  { value: 'new',       label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'proposal',  label: 'Proposal' },
  { value: 'converted', label: 'Converted' },
  { value: 'lost',      label: 'Lost' },
  { value: 'inactive',  label: 'Inactive' },
]);

/**
 * Labels for every short code a contact row has ever been able to hold.
 *
 * ⚠ WIDER THAN LEGACY_CONTACT_STAGES ON PURPOSE. mig-137 dropped the CHECK on
 * pipeline_stage, and routes/gohighlevel.js:1802-1807 has its own keyword
 * ladder, so rows carrying the opportunity slugs exist. Labelling is a
 * read-side concern and must cover everything in the column; OFFERING is a
 * write-side concern and must not. Two lists, deliberately.
 */
export const LEGACY_STAGE_LABELS = Object.freeze({
  lead: 'Lead', contacted: 'Contacted', qualified: 'Qualified',
  proposal: 'Proposal', negotiation: 'Negotiation',
  closed_won: 'Closed Won', closed_lost: 'Closed Lost',
  new: 'New', converted: 'Converted', lost: 'Lost', inactive: 'Inactive',
});

/** A stage row from crm_pipeline_stages → a picker option. */
function optionFromStage(s) {
  return {
    value: s.name,
    label: s.name,
    color: s.color || null,
    isWin: !!s.is_win,
    isLoss: !!s.is_loss,
  };
}

/**
 * Turn a `crm:contacts:stage-options` payload into options to render.
 *
 * ⛔ NEVER RETURNS AN EMPTY LIST. A failed or empty read falls back to the
 * legacy codes, because a stage control with no options is indistinguishable
 * from a contact with no stages and silently removes a working affordance —
 * the catch-to-empty shape that turns a code defect into a clean result.
 *
 * @param payload      what the server returned (or null/undefined on failure)
 * @param currentValue the contact's CURRENT pipeline_stage, if any
 */
export function stageOptionsFrom(payload, currentValue) {
  const stages = (payload && Array.isArray(payload.stages)) ? payload.stages : [];
  const fromPipeline = stages.length > 0;

  const options = fromPipeline
    ? stages.map(optionFromStage)
    : LEGACY_CONTACT_STAGES.map((o) => ({ ...o, color: null, isWin: false, isLoss: false }));

  /*
   * ⛔⛔ THE VALUE THE ROW ACTUALLY HOLDS IS ALWAYS SHOWN, even when it is not
   * one of the options. A contact can hold a short code while sitting on a
   * pipeline (gohighlevel.js:1198 only writes over 'new'/NULL, so a contact
   * contacted before its deal existed keeps its code), and it can hold a stage
   * from a pipeline it has since left. Dropping the value from the list makes
   * a <select> render as if the contact were on its FIRST stage — a display
   * that silently misreports the record, and one click from writing that lie
   * back. Shown, flagged, and not offered as a destination.
   */
  const has = currentValue != null && currentValue !== ''
    && options.some((o) => o.value === currentValue);
  if (currentValue != null && currentValue !== '' && !has) {
    options.unshift({
      value: currentValue,
      label: LEGACY_STAGE_LABELS[currentValue] || currentValue,
      color: null, isWin: false, isLoss: false,
      isUnlisted: true,
    });
  }

  return {
    source: fromPipeline ? 'pipeline' : 'legacy',
    pipelineId: (payload && payload.pipelineId) || null,
    pipelineName: (payload && payload.pipelineName) || null,
    options,
  };
}

/** Display label for one stage value, given the resolved options. */
export function labelForStage(value, options) {
  if (value == null || value === '') return '';
  const hit = (options || []).find((o) => o.value === value);
  if (hit) return hit.label;
  return LEGACY_STAGE_LABELS[value] || value;
}

export default stageOptionsFrom;
